import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import { formatDate } from "../../utils/date";
import styles from "./Payments.module.scss";
import OrderService from "../../api/services/orders.service";
import { PaymentTypes } from "../../api/types/orders.types";
import Loading from "../../components/common/loading/Loading";

const Payments = () => {
  const { t } = useTranslation("translation");

  const StatusButton = ({ status }: { status: string }) => {
    switch (status) {
      case "1":
        return (
          <div className={`${styles.status} ${styles.deliveredBtn}`}>
            {t("payments.status.not_completed") as string}
          </div>
        );
      case "2":
        return (
          <div className={`${styles.status} ${styles.completeBtn}`}>
            {t("payments.status.success") as string}
          </div>
        );
      case "3":
        return (
          <div className={`${styles.status} ${styles.deliveryBtn}`}>
            {t("payments.status.failed") as string}
          </div>
        );
      default:
        return (
          <div className={styles.status}>
            {t("payments.status.unknown") as string}
          </div>
        );
    }
  };

  const columns: Column[] = [
    {
      key: "user",
      header: t("payments.table.client") as string,
      render: (_, row) => (
        <div className={styles.user_cell}>
          {row.user.profile_photo ? (
            <img
              src={row?.user?.profile_photo}
              alt={row?.user?.first_name}
              className={styles.user_avatar}
            />
          ) : (
            <div className={styles.no_image}>
              {row?.user?.first_name[0]}
              {row?.user?.last_name[0]}
            </div>
          )}
          <div className={styles.user_info}>
            <p className={styles.user_name}>
              {row?.user?.first_name} {row?.user?.last_name}
            </p>
            <a href={`tel:${row?.user?.phone}`} className={styles.user_phone}>
              {row?.user?.phone || ""}
            </a>
            <a
              href={`mailto:${row?.user?.email}`}
              className={styles.user_email}
            >
              {row?.user?.email}
            </a>
          </div>
        </div>
      ),
    },
    {
      key: "order_id",
      header: t("payments.table.order_id") as string,
      render: (v) => <p className={styles.box}>{v}</p>,
    },
    {
      key: "transaction_id",
      header: t("payments.table.transaction_id") as string,
      render: (v) => <p className={styles.content}>{v}</p>,
    },
    {
      key: "amount",
      header: t("payments.table.amount") as string,
      render: (_, row) => (
        <p>
          {row.amount}
          {row.currency_name}
        </p>
      ),
    },
    { key: "card_number", header: t("payments.table.card_number") as string },
    {
      key: "created",
      header: t("payments.table.date") as string,
      render: (v) => formatDate(v),
    },
    {
      key: "status",
      header: t("payments.table.status") as string,
      render: (value) => {
        return <StatusButton status={value} />;
      },
    },
  ];

  const [count, setCount] = useState<number | null>(null);
  const [paymnets, setPayments] = useState<PaymentTypes[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrderService.getAllPayments(page, limit);
      setPayments(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  if (loading) return <Loading />;
  return (
    <div className={styles.delivery}>
      <TitleUpdater title={t("payments.title") as string} />
      <DataTable
        table_title={t("payments.title") as string}
        data={paymnets}
        columns={columns}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />
    </div>
  );
};

export default Payments;
